import React, { useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import qs from 'query-string'
import { LocationDescriptor } from 'history'

import { routes } from '@sketch/constants'

import { useGetPublicWorkspaceFromTokenQuery } from '@sketch/gql-types'

import {
  Description,
  WorkspaceLogo,
  Wrapper,
  ItemWrapper,
  InviterAvatar,
} from './RedirectBadge.styles'
import { normalizeToLocationObject } from 'utils/route'
import { Inviter } from 'modules/shares/AcceptShareInviteView/types'

const getFirstArgument = (field: string | string[]) =>
  Array.isArray(field) ? field[0] : field

const getBadgeType = (pathname?: string) => {
  if (routes.WORKSPACE_INVITE.template() === pathname) {
    return 'workspace-invite' as const
  } else if (
    routes.SHARE_ACCEPT_INVITE_SIGN_IN.template() === pathname ||
    routes.SHARE_ACCEPT_INVITE_SIGN_UP.template() === pathname
  ) {
    return 'document-invite' as const
  } else if (routes.PROJECT_ACCEPT_INVITE.template() === pathname) {
    return 'project-invite'
  }
}

type RedirectBadgeProps =
  | { from: LocationDescriptor }
  | DocumentInviteProps
  | ProjectInviteProps

const RedirectBadge: React.FC<RedirectBadgeProps> = props => {
  const { pathname, search } = normalizeToLocationObject(props.from)
  const query: { token?: string } = qs.parse(search || '')

  const badgeType = getBadgeType(pathname)

  if (badgeType === 'workspace-invite' && query.token) {
    return <WorkspaceInvite inviteToken={getFirstArgument(query.token)} />
  }

  if (badgeType === 'document-invite') {
    return <DocumentInvite {...(props as DocumentInviteProps)} />
  }

  if (badgeType === 'project-invite') {
    return <ProjectInvite {...(props as ProjectInviteProps)} />
  }

  return null
}

/**
 * Redirect Badge Variants
 */

// Workspace Invite Badge
interface WorkspaceInviteProps {
  inviteToken: string
}

const WorkspaceInvite = ({ inviteToken }: WorkspaceInviteProps) => {
  const nodeRef = useRef(null)
  const { data, error } = useGetPublicWorkspaceFromTokenQuery({
    variables: { inviteToken },
  })

  if (error) {
    return null
  }

  const shouldShowBadge = Boolean(data?.getPublicWorkspaceFromToken.name)

  return (
    <CSSTransition
      nodeRef={nodeRef}
      in={shouldShowBadge}
      timeout={200}
      mountOnEnter={true}
      unmountOnExit={true}
      classNames="badge"
    >
      <Wrapper>
        <ItemWrapper ref={nodeRef}>
          <WorkspaceLogo
            workspaceName={data?.getPublicWorkspaceFromToken.name}
            src={data?.getPublicWorkspaceFromToken.avatar?.small}
            size="32px"
          />
          <Description>
            You’ve been invited to{' '}
            <b>{data?.getPublicWorkspaceFromToken.name}</b>
          </Description>
        </ItemWrapper>
      </Wrapper>
    </CSSTransition>
  )
}

// Document Invite Badge
interface DocumentInviteProps {
  from: LocationDescriptor
  documentInvitation: {
    documentName: string
    inviter?: Inviter
  }
}

const DocumentInvite = ({ documentInvitation }: DocumentInviteProps) => {
  const { documentName, inviter } = documentInvitation

  return (
    <Wrapper>
      <ItemWrapper aria-label="redirect-badge">
        {inviter && (
          <InviterAvatar
            size="32px"
            src={inviter?.avatar?.large || undefined}
            name={inviter.name || '?' /** Name should always be there */}
          />
        )}
        <Description>
          <b>{inviter?.name}</b> wants to share the document &ldquo;
          <b>{documentName}</b>&rdquo; with you
        </Description>
      </ItemWrapper>
    </Wrapper>
  )
}

// Project Invite Badge
interface ProjectInviteProps {
  from: LocationDescriptor
  projectInvitation: {
    projectName: string
    inviter?: Inviter
  }
}

const ProjectInvite = ({ projectInvitation }: ProjectInviteProps) => {
  const { projectName, inviter } = projectInvitation

  return (
    <Wrapper>
      <ItemWrapper aria-label="redirect-badge">
        {inviter && (
          <InviterAvatar
            size="32px"
            src={inviter?.avatar?.large || undefined}
            name={inviter.name || '?' /** Name should always be there */}
          />
        )}
        <Description>
          <b>{inviter?.name}</b> wants to share the &ldquo;
          <b>{projectName}</b>&rdquo; project with you
        </Description>
      </ItemWrapper>
    </Wrapper>
  )
}

export default RedirectBadge
